import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Icon } from '@iconify/react';

/* ─────────────── constants ─────────────── */
const DAYS = [
    { label: 'Min', value: 'Minggu' },
    { label: 'Sen', value: 'Senin' },
    { label: 'Sel', value: 'Selasa' },
    { label: 'Rab', value: 'Rabu' },
    { label: 'Kam', value: 'Kamis' },
    { label: 'Jum', value: 'Jumat' },
    { label: 'Sab', value: 'Sabtu' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
const ITEM_H = 48;

const getCurrentTime = () => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
};

/* ─────────────── ScrollColumn (drum roll) ─────────────── */
function ScrollColumn({ items, value, onChange }) {
    const ref = useRef(null);
    const isDragging = useRef(false);
    const startY = useRef(0);
    const startScroll = useRef(0);
    const idx = items.indexOf(value);

    useEffect(() => {
        if (ref.current) ref.current.scrollTop = idx * ITEM_H;
    }, [idx]);

    const handleScroll = useCallback(() => {
        if (!ref.current) return;
        const newIdx = Math.round(ref.current.scrollTop / ITEM_H);
        const clamped = Math.max(0, Math.min(items.length - 1, newIdx));
        if (items[clamped] !== value) onChange(items[clamped]);
    }, [items, value, onChange]);

    const onMouseDown = (e) => {
        isDragging.current = true;
        startY.current = e.clientY;
        startScroll.current = ref.current.scrollTop;
        e.preventDefault();
    };
    const onMouseMove = useCallback((e) => {
        if (!isDragging.current) return;
        ref.current.scrollTop = startScroll.current - (e.clientY - startY.current);
    }, []);
    const onMouseUp = useCallback(() => { isDragging.current = false; }, []);

    useEffect(() => {
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [onMouseMove, onMouseUp]);

    return (
        <div
            ref={ref}
            onScroll={handleScroll}
            onMouseDown={onMouseDown}
            className="overflow-y-scroll select-none"
            style={{
                height: ITEM_H * 5,
                scrollSnapType: 'y mandatory',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                cursor: 'grab',
            }}
        >
            <div style={{ paddingTop: ITEM_H * 2, paddingBottom: ITEM_H * 2 }}>
                {items.map((item) => (
                    <div
                        key={item}
                        onClick={() => {
                            onChange(item);
                            if (ref.current) ref.current.scrollTop = items.indexOf(item) * ITEM_H;
                        }}
                        style={{
                            height: ITEM_H,
                            scrollSnapAlign: 'center',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: item === value ? 28 : 18,
                            fontWeight: item === value ? 700 : 400,
                            color: item === value ? '#111827' : '#9CA3AF',
                            transition: 'all 0.15s',
                            cursor: 'pointer',
                        }}
                    >
                        {item}
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ─────────────── TimePicker popup ─────────────── */
function TimePicker({ value, onChange, onClose }) {
    const [h, m] = (value || getCurrentTime()).split(':');
    const [hour, setHour] = useState(h || '00');
    const [minute, setMinute] = useState(m || '00');

    return (
        <div
            className="fixed inset-0 z-[300] flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-sm"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="bg-white w-full max-w-xs rounded-3xl shadow-2xl overflow-hidden">
                <div className="relative px-6 pt-4 pb-2 flex items-center gap-2">
                    {/* highlight bar */}
                    <div
                        className="absolute left-0 right-0 pointer-events-none"
                        style={{
                            top: '50%',
                            transform: 'translateY(-50%)',
                            height: ITEM_H,
                            background: 'rgba(6,182,212,0.08)',
                            borderTop: '1.5px solid rgba(6,182,212,0.25)',
                            borderBottom: '1.5px solid rgba(6,182,212,0.25)',
                        }}
                    />
                    <div className="flex-1">
                        <ScrollColumn items={HOURS} value={hour} onChange={setHour} />
                    </div>
                    <span className="text-3xl font-bold text-gray-800">:</span>
                    <div className="flex-1">
                        <ScrollColumn items={MINUTES} value={minute} onChange={setMinute} />
                    </div>
                </div>
                <div className="flex border-t border-gray-100">
                    <button
                        onClick={onClose}
                        className="flex-1 py-4 text-sm font-bold text-gray-500 hover:bg-gray-50 transition-colors"
                    >Batal</button>
                    <button
                        onClick={() => { onChange(`${hour}:${minute}`); onClose(); }}
                        className="flex-1 py-4 text-sm font-bold text-white bg-gradient-to-r from-[#06B6D4] to-[#038DA5] hover:opacity-90 transition-opacity"
                    >Gunakan</button>
                </div>
            </div>
        </div>
    );
}

/* ─────────────── JadwalPicker ─────────────── */
/**
 * Layout:
 *  [Pilih Hari field]  ← click opens day-pill popup
 *  [day popup]         ← only visible when open
 *  [Mulai] [Selesai ⏱] ← always visible, separate row
 */
function JadwalPicker({ selectedDays, startTime, endTime, onToggleDay, onOpenTimePicker, jadwalDisplay }) {
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div ref={wrapperRef} className="relative">
            <label className="block text-sm font-bold text-gray-700 mb-2">Jadwal Praktik</label>

            {/* "Pilih Hari" trigger */}
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className={`w-full h-[52px] px-4 bg-white border rounded-2xl flex items-center text-sm font-body transition-all text-left mb-3 ${open ? 'border-cyan-400 ring-2 ring-cyan-500/20' : 'border-gray-200'
                    } ${jadwalDisplay ? 'text-gray-700' : 'text-gray-400'}`}
            >
                {jadwalDisplay || 'Pilih Hari'}
            </button>

            {/* Day-pill popup — appears below field when open */}
            {open && (
                <div className="absolute left-0 right-0 z-[150] mt-[-8px] bg-white rounded-2xl shadow-xl p-3">
                    <div className="flex items-center gap-0 overflow-x-auto pb-1 scrollbar-hide">
                        {DAYS.map((day, idx) => {
                            const isSelected = selectedDays.includes(day.value);

                            // To show a connected range, we check neighbors
                            const prevDaySelect = idx > 0 && selectedDays.includes(DAYS[idx - 1].value);
                            const nextDaySelect = idx < DAYS.length - 1 && selectedDays.includes(DAYS[idx + 1].value);

                            // Is it an "edge" of the selection?
                            const isEdge = isSelected && (!prevDaySelect || !nextDaySelect);
                            const isFullRange = isSelected && prevDaySelect && nextDaySelect;

                            return (
                                <div key={day.value} className="flex-1 relative flex items-center justify-center py-1">
                                    {/* Range track background */}
                                    {isSelected && (
                                        <div className={`absolute inset-y-1 bg-cyan-50 ${!prevDaySelect ? 'left-1/2 rounded-l-full' : 'left-0'
                                            } ${!nextDaySelect ? 'right-1/2 rounded-r-full' : 'right-0'
                                            }`} />
                                    )}

                                    <button
                                        type="button"
                                        onClick={() => onToggleDay(day.value)}
                                        className={`relative z-10 w-10 h-10 rounded-full text-[13px] font-bold transition-all flex items-center justify-center ${isSelected
                                                ? 'bg-[#06B6D4] text-white shadow-md shadow-cyan-500/20'
                                                : 'text-gray-500 hover:bg-gray-100'
                                            }`}
                                    >
                                        {day.label}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Time inputs — always visible, separate from the day popup */}
            <div className="flex items-stretch h-[52px] bg-white border border-gray-200 rounded-2xl overflow-hidden">
                <button
                    type="button"
                    onClick={() => onOpenTimePicker('start')}
                    className="flex-1 px-4 text-sm font-body text-left hover:bg-gray-50 transition-colors border-r border-gray-100"
                >
                    <span className={startTime ? 'text-gray-700' : 'text-gray-400'}>
                        {startTime || 'Mulai'}
                    </span>
                </button>
                <button
                    type="button"
                    onClick={() => onOpenTimePicker('end')}
                    className="flex-1 px-4 text-sm font-body text-left hover:bg-gray-50 transition-colors"
                >
                    <span className={endTime ? 'text-gray-700' : 'text-gray-400'}>
                        {endTime || 'Selesai'}
                    </span>
                </button>
                <div
                    onClick={() => onOpenTimePicker('start')}
                    className="w-[52px] bg-gray-50 flex items-center justify-center border-l border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
                >
                    <Icon icon="material-symbols:schedule-outline" className="text-gray-500 text-2xl" />
                </div>
            </div>
        </div>
    );
}

/* ─────────────── helpers ─────────────── */
const buildJadwal = (days, start, end) => {
    if (!days.length) return '';
    const orderMap = { Minggu: 0, Senin: 1, Selasa: 2, Rabu: 3, Kamis: 4, Jumat: 5, Sabtu: 6 };
    const sorted = [...days].sort((a, b) => orderMap[a] - orderMap[b]);
    const dayPart = sorted.length > 1
        ? `${sorted[0]}-${sorted[sorted.length - 1]}`
        : sorted[0];
    return `${dayPart} ${start}-${end}`;
};

const parseJadwal = (jadwal) => {
    if (!jadwal) return { days: [], start: '08:00', end: '16:00' };
    const normalized = jadwal.replace(/\./g, ':');
    const m = normalized.match(/^(.+?)\s+(\d{2}:\d{2})-(\d{2}:\d{2})$/);
    if (m) {
        const dayRange = m[1];
        const start = m[2];
        const end = m[3];
        const order = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
        if (dayRange.includes('-')) {
            const [from, to] = dayRange.split('-').map(d => d.trim());
            const fi = order.indexOf(from);
            const ti = order.indexOf(to);
            if (fi !== -1 && ti !== -1) return { days: order.slice(fi, ti + 1), start, end };
        }
        const single = order.find(d => d === dayRange.trim());
        return { days: single ? [single] : [], start, end };
    }
    return { days: [], start: '08:00', end: '16:00' };
};

/* ─────────────── DokterModal ─────────────── */
const DokterModal = ({ isOpen, onClose, onSubmit, editData = null, poliList = [] }) => {
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        nama_dokter: '',
        email: '',
        id_poli: '',
        username: '',
        password: '',
        selectedDays: [],
        startTime: getCurrentTime(),
        endTime: getCurrentTime(),
        status: 'aktif',
        foto: null,
        fotoPreview: '',
    });

    const [showTimePicker, setShowTimePicker] = useState(null); // 'start' | 'end' | null

    useEffect(() => {
        if (editData) {
            const { days, start, end } = parseJadwal(editData.jadwal);
            setFormData({
                nama_dokter: editData.nama_dokter || '',
                email: editData.email || editData.user?.email || '',
                id_poli: editData.id_poli || '',
                username: editData.user?.username || '',
                password: '',
                selectedDays: days,
                startTime: start,
                endTime: end,
                status: editData.status || 'aktif',
                foto: null,
                fotoPreview: editData.foto || '',
            });
        } else {
            setFormData({
                nama_dokter: '',
                email: '',
                id_poli: '',
                username: '',
                password: '',
                selectedDays: [],
                startTime: getCurrentTime(),
                endTime: getCurrentTime(),
                status: 'aktif',
                foto: null,
                fotoPreview: '',
            });
        }
    }, [editData, isOpen]);

    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : 'unset';
        return () => { document.body.style.overflow = 'unset'; };
    }, [isOpen]);

    if (!isOpen) return null;

    const toggleDay = (dayValue) => {
        setFormData(prev => {
            const order = DAYS.map(d => d.value);
            const currentIdx = order.indexOf(dayValue);

            // Logic: 
            // 1. If 0 or >1 days currently selected, start a new range with 1 day
            // 2. If 1 day selected, create range between first and second pick

            if (prev.selectedDays.length === 0 || prev.selectedDays.length > 1) {
                return { ...prev, selectedDays: [dayValue] };
            }

            const firstDay = prev.selectedDays[0];
            const firstIdx = order.indexOf(firstDay);

            if (firstIdx === currentIdx) {
                return { ...prev, selectedDays: [] };
            }

            const start = Math.min(firstIdx, currentIdx);
            const end = Math.max(firstIdx, currentIdx);
            const range = order.slice(start, end + 1);

            return { ...prev, selectedDays: range };
        });
    };

    const jadwalDisplay = buildJadwal(formData.selectedDays, formData.startTime, formData.endTime);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({
                ...prev,
                foto: file,
                fotoPreview: URL.createObjectURL(file),
            }));
        }
    };

    const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
    const handleDrop = (e) => {
        e.preventDefault(); e.stopPropagation();
        const file = e.dataTransfer.files[0];
        if (file && file.type.startsWith('image/')) {
            setFormData(prev => ({
                ...prev,
                foto: file,
                fotoPreview: URL.createObjectURL(file),
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await onSubmit({ ...formData, jadwal: jadwalDisplay });
            onClose();
        } catch {
            // Parent shows error; keep modal open
        }
    };

    return (
        <>
            {/* ── Main modal overlay ── */}
            <div
                className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm transition-opacity"
                onClick={(e) => e.target === e.currentTarget && onClose()}
            >
                <div className="bg-white w-full max-w-[480px] max-h-[90vh] rounded-[32px] shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300 flex flex-col">
                    <form onSubmit={handleSubmit} className="flex flex-col min-h-0">

                        {/* Header */}
                        <div className="flex justify-between items-center px-8 pt-8 pb-2 flex-shrink-0">
                            <h2 className="text-2xl font-bold text-gray-900">
                                {editData ? 'Edit Dokter' : 'Tambah Dokter'}
                            </h2>
                            <button
                                type="button"
                                onClick={onClose}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#06B6D4" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>

                        {/* Scrollable body */}
                        <div className="flex-1 overflow-y-auto px-8 py-4 space-y-5 scrollbar-thin scrollbar-thumb-gray-200">

                            {/* Foto */}
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Foto</label>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    accept="image/*"
                                    className="hidden"
                                />
                                <div
                                    className="w-full h-[110px] bg-gray-50 border border-gray-200 rounded-2xl flex flex-col items-center justify-center cursor-pointer hover:bg-gray-100 hover:border-cyan-300 transition-all text-gray-400 overflow-hidden"
                                    onClick={() => fileInputRef.current.click()}
                                    onDragOver={handleDragOver}
                                    onDrop={handleDrop}
                                >
                                    {formData.fotoPreview ? (
                                        <img src={formData.fotoPreview} alt="Preview" className="w-full h-full object-cover" />
                                    ) : (
                                        <span className="text-sm font-body">Tarik File atau Klik untuk Menggunggah</span>
                                    )}
                                </div>
                            </div>

                            {/* Nama Dokter */}
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Nama Dokter</label>
                                <input
                                    type="text"
                                    name="nama_dokter"
                                    value={formData.nama_dokter}
                                    onChange={handleInputChange}
                                    placeholder="Masukkan Nama Dokter"
                                    className="w-full h-[52px] px-4 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-400 text-gray-700 font-body transition-all placeholder:text-gray-400 text-sm"
                                    required
                                />
                            </div>

                            {/* Email akun (login halaman dokter) */}
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Email</label>
                                <p className="text-xs text-gray-400 mb-2 font-body">
                                    Email untuk login dokter di website (sama dengan akun pengguna).
                                </p>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="nama@email.com"
                                    className="w-full h-[52px] px-4 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-400 text-gray-700 font-body transition-all placeholder:text-gray-400 text-sm"
                                    required
                                    autoComplete="email"
                                />
                            </div>

                            {/* Poli */}
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Poli</label>
                                <div className="relative">
                                    <select
                                        name="id_poli"
                                        value={formData.id_poli}
                                        onChange={handleInputChange}
                                        className="w-full h-[52px] px-4 bg-white border border-gray-200 rounded-2xl appearance-none focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-400 text-gray-700 font-body transition-all cursor-pointer text-sm"
                                        required
                                    >
                                        <option value="">Pilih Poli</option>
                                        {poliList.map((poli) => (
                                            <option key={poli.id_poli} value={poli.id_poli}>{poli.nama_poli}</option>
                                        ))}
                                    </select>
                                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="6 9 12 15 18 9" />
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            {/* Nama Pengguna */}
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Nama Pengguna</label>
                                <input
                                    type="text"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleInputChange}
                                    placeholder="Buat Nama Pengguna (tanpa spasi)"
                                    className="w-full h-[52px] px-4 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-400 text-gray-700 font-body transition-all placeholder:text-gray-400 text-sm"
                                    required
                                />
                            </div>

                            {/* Kata Sandi */}
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">
                                    Kata Sandi{' '}
                                    {editData && (
                                        <span className="text-xs text-gray-400 font-normal">
                                            (Kosongkan jika tidak ingin mengubah)
                                        </span>
                                    )}
                                </label>
                                <input
                                    type="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    placeholder="••••••••"
                                    className="w-full h-[52px] px-4 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-400 text-gray-700 font-body transition-all placeholder:text-gray-400 text-sm"
                                    required={!editData}
                                />
                            </div>

                            {/* Jadwal Praktik — popup picker */}
                            <JadwalPicker
                                selectedDays={formData.selectedDays}
                                startTime={formData.startTime}
                                endTime={formData.endTime}
                                onToggleDay={toggleDay}
                                onOpenTimePicker={(which) => setShowTimePicker(which)}
                                jadwalDisplay={jadwalDisplay}
                            />

                            {/* Status */}
                            <div className="pb-4">
                                <label className="block text-sm font-bold text-gray-700 mb-3">Status</label>
                                <div className="flex gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, status: 'aktif' }))}
                                        className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all ${formData.status === 'aktif'
                                            ? 'bg-[#0DBC94] text-white shadow-lg shadow-green-500/30'
                                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                            }`}
                                    >Aktif</button>
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, status: 'nonaktif' }))}
                                        className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all ${formData.status === 'nonaktif'
                                            ? 'bg-[#F64E60] text-white shadow-lg shadow-red-500/30'
                                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                            }`}
                                    >Non-Aktif</button>
                                </div>
                            </div>
                        </div>

                        {/* Submit button */}
                        <div className="px-8 py-5 flex-shrink-0 border-t border-gray-100">
                            <button
                                type="submit"
                                className="w-full py-4 bg-gradient-to-r from-[#06B6D4] to-[#038DA5] text-white font-bold rounded-2xl hover:opacity-90 transition-all shadow-lg shadow-cyan-500/30 active:scale-[0.98]"
                            >
                                {editData ? 'Simpan Perubahan' : 'Tambah Dokter'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* ── Time Picker popup (z-[300] above modal) ── */}
            {showTimePicker && (
                <TimePicker
                    value={showTimePicker === 'start' ? formData.startTime : formData.endTime}
                    onChange={(t) =>
                        setFormData(prev => ({
                            ...prev,
                            ...(showTimePicker === 'start' ? { startTime: t } : { endTime: t }),
                        }))
                    }
                    onClose={() => setShowTimePicker(null)}
                />
            )}
        </>
    );
};

export default DokterModal;
